/**
 * Oxidation numbers by the textbook rules, in priority order, with the rule behind each one.
 *
 * ## The rules, highest priority first
 *
 * 1. An atom in an element has oxidation number 0; a monatomic ion's equals its charge.
 * 2. The numbers add up to the charge of the species (0 for a neutral compound).
 * 3. Fluorine is −1.
 * 4. Group 1 metals are +1 and group 2 metals +2.
 * 5. Hydrogen is +1, but −1 when bonded only to metals (or boron): NaH, CaH₂, LiAlH₄.
 * 6. Oxygen is −2, except −1 in peroxides, −½ in superoxides, −⅓ in ozonides, and positive
 *    with fluorine (+2 in OF₂, +1 in O₂F₂).
 * 7. Cl, Br and I are −1, except with oxygen, fluorine or a more electronegative halogen.
 * 8. Whatever is left is found from rule 2.
 *
 * This is the order of OpenStax Chemistry 2e §4.2 (elements, ions, H, O, halogens, sum)
 * with the group rules added. It is a bookkeeping shortcut for the IUPAC definition (Karen,
 * McArdle and Takats, Pure Appl. Chem. 88, 831, 2016), in which the bonding electrons go to
 * the more electronegative atom and are shared equally between identical atoms.
 *
 * ## Where the rules run out
 *
 * - Two or more elements without a fixed rule (FeSO₄: Fe and S). Common polyatomic ions
 *   (SO₄²⁻, NO₃⁻, …) are recognised first, which fixes the cation; failing that, the more
 *   electronegative of two elements takes its usual negative state (CS₂, SiC); failing that,
 *   the formula is reported as ambiguous.
 * - One element in several environments (S₂O₃²⁻, Fe₃O₄, carbon in organic molecules): the
 *   rules give an *average*, which is flagged.
 * - A peroxide hidden in a formula whose only unknown is a metal (CrO₅): the answer
 *   exceeds +8 and is flagged.
 */

import {
  ELECTRONEGATIVITY,
  GROUP_1,
  GROUP_2,
  HALOGENS,
  KNOWN_IONS,
  type KnownIon,
  MAX_STATE,
  NEAR_HYDROGEN,
  NEGATIVE_STATE,
  NONMETALS,
  OTHER_HALOGENS,
} from "@/lib/tool-kit/chem-reactions/oxidation-data";
import {
  add,
  type Fraction,
  frac,
  isZero,
  mul,
  sub,
} from "@/lib/tool-kit/chem-reactions/rational";
import {
  countAtoms,
  type ElementCount,
  type FormulaNode,
  formulaToUnicode,
  type ParsedFormula,
  parseFormula,
} from "@/lib/tool-kit/chemistry/formula";

export type RuleKey =
  | "free"
  | "monatomic"
  | "fluorine"
  | "group1"
  | "group2"
  | "hydrogen"
  | "hydride"
  | "oxygen"
  | "peroxide"
  | "superoxide"
  | "ozonide"
  | "oxygenFluoride"
  | "halogen"
  | "sum"
  | "electronegativity"
  | "ion";

export type FlagKey =
  | "peroxide"
  | "superoxide"
  | "ozonide"
  | "hydride"
  | "oxygenFluoride"
  | "average"
  | "organic"
  | "mixed"
  | "nearHydrogen"
  | "tooHigh";

export interface Assignment {
  symbol: string;
  count: number;
  state: Fraction;
  rule: RuleKey;
  flags: FlagKey[];
}

export interface Environment {
  /** The species as Unicode text: SO₄²⁻, Fe²⁺, H₂O. */
  label: string;
  charge: number;
  /** How many of them the formula holds (3 for the sulfate in Fe₂(SO₄)₃). */
  multiplicity: number;
  assignments: Assignment[];
}

export type Failure =
  | { ok: false; reason: "parse"; token: string; code: string }
  | { ok: false; reason: "inconsistent" }
  | { ok: false; reason: "ambiguous"; elements: string[] };

export type OxidationResult =
  | { ok: true; environments: Environment[]; formula: string; charge: number }
  | Failure;

/* ----------------------------------------------------------- arithmetic */

const ZERO = frac(0);

function whole(state: Fraction): boolean {
  return state.d === frac(1).d;
}

function toNumber(state: Fraction): number {
  return Number(state.n) / Number(state.d);
}

/** `state ÷ count` as an exact fraction. */
function perAtom(total: Fraction, count: number): Fraction {
  return frac(total.n, total.d * BigInt(count));
}

function weighted(assignments: readonly Assignment[]): Fraction {
  return assignments.reduce(
    (sum, a) => add(sum, mul(a.state, frac(a.count))),
    ZERO
  );
}

function countOf(counts: readonly ElementCount[], symbol: string): number {
  return counts.find((c) => c.symbol === symbol)?.count ?? 0;
}

function sameCounts(
  a: readonly ElementCount[],
  b: readonly ElementCount[]
): boolean {
  return (
    a.length === b.length &&
    a.every((entry) => countOf(b, entry.symbol) === entry.count)
  );
}

function labelFor(counts: readonly ElementCount[], charge: number): string {
  const nodes: FormulaNode[] = counts.map((c) => ({
    kind: "element",
    symbol: c.symbol,
    count: c.count,
  }));
  const formula: ParsedFormula = {
    parts: [{ coefficient: 1, coefficientText: "", nodes }],
    charge,
    counts: [...counts],
  };
  return formulaToUnicode(formula);
}

/* ------------------------------------------------------- fixed rules */

interface Context {
  symbols: Set<string>;
  hasO: boolean;
  hasF: boolean;
}

interface Fixed {
  state: Fraction;
  rule: RuleKey;
  flags: FlagKey[];
}

function contextOf(counts: readonly ElementCount[]): Context {
  const symbols = new Set(counts.map((c) => c.symbol));
  return { symbols, hasO: symbols.has("O"), hasF: symbols.has("F") };
}

function halogenIsFixed(symbol: string, ctx: Context): boolean {
  if (ctx.hasO || ctx.hasF) {
    return false;
  }
  const rank = ELECTRONEGATIVITY[symbol] ?? 0;
  return !HALOGENS.some(
    (other) =>
      other !== symbol &&
      ctx.symbols.has(other) &&
      (ELECTRONEGATIVITY[other] ?? 0) > rank
  );
}

function hydrogenRule(ctx: Context): Fixed {
  const others = [...ctx.symbols].filter((s) => s !== "H");
  const metalsOnly =
    others.length > 0 && others.every((s) => !NONMETALS.has(s) || s === "B");
  if (metalsOnly) {
    return { state: frac(-1), rule: "hydride", flags: ["hydride"] };
  }
  const near = others.some((s) => NEAR_HYDROGEN.has(s));
  return {
    state: frac(1),
    rule: "hydrogen",
    flags: near ? ["nearHydrogen"] : [],
  };
}

/** The state a rule fixes for `symbol`, or null when it comes from the sum. */
function fixedState(symbol: string, ctx: Context): Fixed | null {
  if (symbol === "F") {
    return { state: frac(-1), rule: "fluorine", flags: [] };
  }
  if (GROUP_1.has(symbol)) {
    return { state: frac(1), rule: "group1", flags: [] };
  }
  if (GROUP_2.has(symbol)) {
    return { state: frac(2), rule: "group2", flags: [] };
  }
  if (symbol === "H") {
    return hydrogenRule(ctx);
  }
  if (symbol === "O") {
    return ctx.hasF ? null : { state: frac(-2), rule: "oxygen", flags: [] };
  }
  if (OTHER_HALOGENS.has(symbol) && halogenIsFixed(symbol, ctx)) {
    return { state: frac(-1), rule: "halogen", flags: [] };
  }
  return null;
}

/** Peroxide, superoxide or ozonide from the state oxygen needs; null for anything else. */
function oxygenKind(state: Fraction): { rule: RuleKey; flag: FlagKey } | null {
  const text = `${state.n}/${state.d}`;
  const kinds: Record<string, { rule: RuleKey; flag: FlagKey }> = {
    "-1/1": { rule: "peroxide", flag: "peroxide" },
    "-1/2": { rule: "superoxide", flag: "superoxide" },
    "-1/3": { rule: "ozonide", flag: "ozonide" },
  };
  return kinds[text] ?? null;
}

/* --------------------------------------------------------- flat solver */

type FlatResult =
  | { ok: true; assignments: Assignment[] }
  | { ok: false; reason: "inconsistent" }
  | { ok: false; reason: "ambiguous"; elements: string[] };

function soleElement(
  counts: readonly ElementCount[],
  charge: number
): Assignment {
  const [only] = counts;
  const state = frac(charge, only.count);
  let rule: RuleKey = charge === 0 ? "free" : "monatomic";
  const flags: FlagKey[] = [];
  const kind = only.symbol === "O" && only.count > 1 ? oxygenKind(state) : null;
  if (kind) {
    rule = kind.rule;
    flags.push(kind.flag);
  } else if (!whole(state)) {
    flags.push("average");
  }
  return { symbol: only.symbol, count: only.count, state, rule, flags };
}

function solvedFlags(
  only: ElementCount,
  state: Fraction,
  counts: readonly ElementCount[]
): FlagKey[] {
  const flags: FlagKey[] = [];
  const oxygen = countOf(counts, "O");
  if (!whole(state)) {
    flags.push("average");
  }
  if (only.symbol === "C" && only.count >= 2 && countOf(counts, "H") > 0) {
    flags.push("organic");
  }
  const thio = only.count === 2 && oxygen === 3;
  const tetra = only.count === 4 && oxygen === 6;
  if (only.symbol === "S" && (thio || tetra)) {
    flags.push("mixed");
  }
  if (toNumber(state) > MAX_STATE) {
    flags.push("tooHigh");
  }
  return flags;
}

/** The last unknown element takes whatever makes the total equal the charge. */
function solveLast(
  counts: readonly ElementCount[],
  charge: number,
  fixed: Assignment[],
  only: ElementCount
): Assignment[] {
  const state = perAtom(sub(frac(charge), weighted(fixed)), only.count);
  if (only.symbol === "O") {
    const kind = oxygenKind(state);
    const flags: FlagKey[] = [];
    if (toNumber(state) > 0) {
      flags.push("oxygenFluoride");
    } else if (kind) {
      flags.push(kind.flag);
    }
    return [
      ...fixed,
      { symbol: "O", count: only.count, state, rule: "oxygenFluoride", flags },
    ];
  }
  return [
    ...fixed,
    {
      symbol: only.symbol,
      count: only.count,
      state,
      rule: "sum",
      flags: solvedFlags(only, state, counts),
    },
  ];
}

/** Everything is fixed; if the sum is off, oxygen may be in a peroxide, superoxide or ozonide. */
function reconcileOxygen(
  charge: number,
  assignments: Assignment[]
): FlatResult {
  const target = frac(charge);
  if (isZero(sub(weighted(assignments), target))) {
    return { ok: true, assignments };
  }
  const oxygen = assignments.find(
    (a) => a.symbol === "O" && a.rule === "oxygen"
  );
  if (!oxygen) {
    return { ok: false, reason: "inconsistent" };
  }
  const others = sub(
    weighted(assignments),
    mul(oxygen.state, frac(oxygen.count))
  );
  const state = perAtom(sub(target, others), oxygen.count);
  const kind = oxygenKind(state);
  if (!kind) {
    return { ok: false, reason: "inconsistent" };
  }
  return {
    ok: true,
    assignments: assignments.map((a) =>
      a === oxygen ? { ...a, state, rule: kind.rule, flags: [kind.flag] } : a
    ),
  };
}

/** With two unknowns, the more electronegative takes its usual negative state. */
function byElectronegativity(
  unknown: readonly ElementCount[]
): Assignment | null {
  if (unknown.length !== 2) {
    return null;
  }
  const [first, second] = [...unknown].sort(
    (a, b) =>
      (ELECTRONEGATIVITY[b.symbol] ?? 0) - (ELECTRONEGATIVITY[a.symbol] ?? 0)
  );
  const negative = NEGATIVE_STATE[first.symbol];
  if (
    negative === undefined ||
    ELECTRONEGATIVITY[second.symbol] === undefined
  ) {
    return null;
  }
  return {
    symbol: first.symbol,
    count: first.count,
    state: frac(negative),
    rule: "electronegativity",
    flags: [],
  };
}

export function solveFlat(
  counts: readonly ElementCount[],
  charge: number
): FlatResult {
  if (counts.length === 1) {
    return { ok: true, assignments: [soleElement(counts, charge)] };
  }
  const ctx = contextOf(counts);
  const fixed: Assignment[] = [];
  let unknown: ElementCount[] = [];
  for (const entry of counts) {
    const rule = fixedState(entry.symbol, ctx);
    if (rule) {
      fixed.push({ symbol: entry.symbol, count: entry.count, ...rule });
    } else {
      unknown.push(entry);
    }
  }
  if (unknown.length >= 2) {
    const picked = byElectronegativity(unknown);
    if (!picked) {
      return {
        ok: false,
        reason: "ambiguous",
        elements: unknown.map((e) => e.symbol),
      };
    }
    fixed.push(picked);
    unknown = unknown.filter((e) => e.symbol !== picked.symbol);
  }
  const done =
    unknown.length === 1
      ? {
          ok: true as const,
          assignments: solveLast(counts, charge, fixed, unknown[0]),
        }
      : reconcileOxygen(charge, fixed);
  if (!done.ok) {
    return done;
  }
  const order = counts.map((c) => c.symbol);
  return {
    ok: true,
    assignments: [...done.assignments].sort(
      (a, b) => order.indexOf(a.symbol) - order.indexOf(b.symbol)
    ),
  };
}

/* -------------------------------------------- polyatomic ions inside */

interface IonPattern {
  ion: KnownIon;
  nodes: FormulaNode[];
  counts: ElementCount[];
}

const ION_PATTERNS: IonPattern[] = KNOWN_IONS.map((ion) => {
  const parsed = parseFormula(ion.formula);
  if (!parsed.ok) {
    throw new Error(`unreadable ion ${ion.formula}`);
  }
  return {
    ion,
    nodes: parsed.formula.parts[0].nodes,
    counts: parsed.formula.counts,
  };
}).sort((a, b) => b.nodes.length - a.nodes.length);

interface Piece {
  ion: KnownIon | null;
  counts: ElementCount[];
  multiplicity: number;
  group: FormulaNode | null;
}

function runMatches(
  pattern: IonPattern,
  nodes: readonly FormulaNode[],
  at: number
): boolean {
  if (at + pattern.nodes.length > nodes.length) {
    return false;
  }
  return pattern.nodes.every((expected, offset) => {
    const actual = nodes[at + offset];
    return (
      expected.kind === "element" &&
      actual.kind === "element" &&
      expected.symbol === actual.symbol &&
      expected.count === actual.count
    );
  });
}

function splitPieces(nodes: readonly FormulaNode[]): Piece[] {
  const pieces: Piece[] = [];
  let index = 0;
  while (index < nodes.length) {
    const node = nodes[index];
    if (node.kind === "group") {
      const flat = countAtoms(node.nodes);
      const hit = ION_PATTERNS.find((p) => sameCounts(p.counts, flat));
      pieces.push({
        ion: hit?.ion ?? null,
        counts: hit ? hit.counts : countAtoms([node]),
        multiplicity: hit ? node.count : 1,
        group: hit ? null : node,
      });
      index++;
      continue;
    }
    const run = ION_PATTERNS.find((p) => runMatches(p, nodes, index));
    if (run) {
      pieces.push({
        ion: run.ion,
        counts: run.counts,
        multiplicity: 1,
        group: null,
      });
      index += run.nodes.length;
    } else {
      pieces.push({
        ion: null,
        counts: countAtoms([node]),
        multiplicity: 1,
        group: null,
      });
      index++;
    }
  }
  return pieces;
}

function mergeCounts(
  lists: readonly (readonly ElementCount[])[]
): ElementCount[] {
  const map = new Map<string, number>();
  for (const list of lists) {
    for (const { symbol, count } of list) {
      map.set(symbol, (map.get(symbol) ?? 0) + count);
    }
  }
  return [...map].map(([symbol, count]) => ({ symbol, count }));
}

function environmentFor(
  counts: readonly ElementCount[],
  charge: number,
  multiplicity: number
): Environment | Failure {
  const flat = solveFlat(counts, charge);
  if (!flat.ok) {
    return flat;
  }
  return {
    label: labelFor(counts, charge),
    charge,
    multiplicity,
    assignments: flat.assignments,
  };
}

function isFailure(
  value: Environment | Environment[] | Failure
): value is Failure {
  return "ok" in value;
}

/** Solve the ions, then use their charge to fix what is left over. */
function solveSplit(
  pieces: readonly Piece[],
  charge: number
): Environment[] | Failure {
  const byIon = new Map<string, Piece>();
  for (const piece of pieces) {
    if (piece.ion) {
      const known = byIon.get(piece.ion.formula);
      if (known) {
        known.multiplicity += piece.multiplicity;
      } else {
        byIon.set(piece.ion.formula, { ...piece });
      }
    }
  }
  const environments: Environment[] = [];
  let remaining = charge;
  for (const piece of byIon.values()) {
    const ionCharge = piece.ion?.charge ?? 0;
    remaining -= ionCharge * piece.multiplicity;
    const env = environmentFor(piece.counts, ionCharge, piece.multiplicity);
    if (isFailure(env)) {
      return env;
    }
    environments.push(env);
  }
  const rest = pieces.filter((p) => !p.ion);
  const restCounts = mergeCounts(rest.map((p) => p.counts));
  if (restCounts.length === 0) {
    return remaining === 0
      ? environments
      : { ok: false, reason: "inconsistent" };
  }
  const opened = openGroup(rest, remaining);
  if (opened) {
    return isFailure(opened) ? opened : [...opened, ...environments];
  }
  const env = environmentFor(restCounts, remaining, 1);
  if (isFailure(env)) {
    return env;
  }
  env.assignments = env.assignments.map((a) =>
    a.rule === "sum" || a.rule === "monatomic" ? { ...a, rule: "ion" } : a
  );
  return [env, ...environments];
}

/** One unrecognised bracket group holds the unknowns: open it, e.g. K₄[Fe(CN)₆]. */
function openGroup(
  rest: readonly Piece[],
  charge: number
): Environment[] | Failure | null {
  const groups = rest.filter((p) => p.group);
  const node = groups.length === 1 ? groups[0].group : null;
  if (!node || node.kind !== "group") {
    return null;
  }
  const others = mergeCounts(rest.filter((p) => !p.group).map((p) => p.counts));
  const ctx = contextOf([...others, ...groups[0].counts]);
  let fixedTotal = ZERO;
  for (const entry of others) {
    const fixed = fixedState(entry.symbol, ctx);
    if (!fixed) {
      return null;
    }
    fixedTotal = add(fixedTotal, mul(fixed.state, frac(entry.count)));
  }
  const perGroup = perAtom(sub(frac(charge), fixedTotal), node.count);
  if (!whole(perGroup)) {
    return { ok: false, reason: "ambiguous", elements: [] };
  }
  const inner = solveNodes(node.nodes, Number(perGroup.n));
  if (isFailure(inner)) {
    return inner;
  }
  const outer =
    others.length > 0 ? environmentFor(others, Number(fixedTotal.n), 1) : null;
  if (outer && isFailure(outer)) {
    return outer;
  }
  return outer ? [outer, ...inner] : inner;
}

function unknownElements(counts: readonly ElementCount[]): ElementCount[] {
  const ctx = contextOf(counts);
  return counts.filter((c) => !fixedState(c.symbol, ctx));
}

/** The one unknown element sits in two or more separate places: NH₄NO₃. */
function repeatedUnknown(
  nodes: readonly FormulaNode[],
  counts: readonly ElementCount[]
): boolean {
  const unknown = unknownElements(counts);
  if (unknown.length !== 1) {
    return false;
  }
  const symbol = unknown[0].symbol;
  return (
    nodes.filter((node) => countOf(countAtoms([node]), symbol) > 0).length >= 2
  );
}

function solveNodes(
  nodes: readonly FormulaNode[],
  charge: number
): Environment[] | Failure {
  const counts = countAtoms(nodes);
  const wantsIons =
    unknownElements(counts).length >= 2 || repeatedUnknown(nodes, counts);
  if (wantsIons) {
    const pieces = splitPieces(nodes);
    if (pieces.some((p) => p.ion || p.group)) {
      const split = solveSplit(pieces, charge);
      if (!isFailure(split) || split.reason !== "ambiguous") {
        return split;
      }
    }
  }
  const flat = environmentFor(counts, charge, 1);
  return isFailure(flat) ? flat : [flat];
}

/* ----------------------------------------------------------- public API */

export function assignOxidation(raw: string): OxidationResult {
  const parsed = parseFormula(raw);
  if (!parsed.ok) {
    return {
      ok: false,
      reason: "parse",
      token: parsed.error.token,
      code: parsed.error.code,
    };
  }
  const { formula } = parsed;
  const environments: Environment[] = [];
  for (const [index, part] of formula.parts.entries()) {
    const solved = solveNodes(part.nodes, index === 0 ? formula.charge : 0);
    if (isFailure(solved)) {
      return solved;
    }
    for (const env of solved) {
      environments.push({
        ...env,
        multiplicity: env.multiplicity * part.coefficient,
      });
    }
  }
  return {
    ok: true,
    environments,
    formula: formulaToUnicode(formula),
    charge: formula.charge,
  };
}

/** "+3", "−2", "0", "+8/3". */
export function stateText(state: Fraction): string {
  if (isZero(state)) {
    return "0";
  }
  const positive = toNumber(state) > 0;
  const magnitude = positive ? state.n : -state.n;
  const body = whole(state) ? `${magnitude}` : `${magnitude}/${state.d}`;
  return `${positive ? "+" : "−"}${body}`;
}

const ROMAN = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII"];

/** Stock notation, Fe(III) or Cl(−I), for whole states from −8 to +8; null otherwise. */
export function stockNotation(symbol: string, state: Fraction): string | null {
  if (!whole(state)) {
    return null;
  }
  const value = Number(state.n);
  if (value === 0) {
    return `${symbol}(0)`;
  }
  if (Math.abs(value) > MAX_STATE) {
    return null;
  }
  return `${symbol}(${value < 0 ? "−" : ""}${ROMAN[Math.abs(value)]})`;
}

/** True when the state is a whole number. */
export function isWholeState(state: Fraction): boolean {
  return whole(state);
}

/** The state as a plain number, for sorting and thresholds. */
export function stateValue(state: Fraction): number {
  return toNumber(state);
}
